import { z } from 'zod';
import { currencyCodes, locationNames, type Country, type Currency } from '../locale';
import { FUNDED_BY_NAME_MAX_LENGTH } from '../impact';
import { FLIGHT_CHECKOUT_MAX_ITEMS } from './constants';
import type { UserType } from '../user';

export type FlightFundBreakdown = Array<{ path: string; percentage: number; text: string }>;

export type FlightProduct = {
  prices: Array<{ amount: number; currency: Currency }>;
  minQuantity: number;
  maxQuantity: number;
  fundBreakdown: FlightFundBreakdown;
};

export const flightMetadataSchema = z
  .object({
    originCode: z.string().min(1),
    destinationCode: z.string().min(1),
    distance: z.number().nonnegative(),
    cabinClass: z.enum(['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST']),
    emissions: z.number().positive(),
    passengers: z.number().int().positive(),
    departureYear: z.number().int(),
    departureDate: z.string().optional(),
    flightNumber: z.string().optional(),
  })
  .strict();

export const flightCheckoutLineSchema = z
  .object({
    quantity: z.number().positive(),
    fundedBy: z.string().max(FUNDED_BY_NAME_MAX_LENGTH),
    flightMetadata: flightMetadataSchema,
  })
  .strict();

export const flightCheckoutPayloadSchema = z
  .object({
    currency: z.enum(currencyCodes),
    flights: z.array(flightCheckoutLineSchema).min(1).max(FLIGHT_CHECKOUT_MAX_ITEMS),
    idempotencyKey: z.string().min(1).max(191),
    utmParams: z.record(z.string(), z.string().optional()).optional(),
  })
  .strict();

const countrySchema = z.custom<Country>(
  value => typeof value === 'string' && Object.prototype.hasOwnProperty.call(locationNames, value),
);

export const flightCheckoutWithRegistrationPayloadSchema = z
  .object({
    customer: z
      .object({
        emailAddress: z.string().email(),
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        location: countrySchema,
      })
      .strict(),
    billingAddress: z
      .object({
        companyName: z.string().optional(),
        address1: z.string().min(1),
        address2: z.string().optional(),
        city: z.string().min(1),
        postcode: z.string().min(1),
        country: countrySchema,
      })
      .strict(),
    basket: flightCheckoutPayloadSchema,
    paymentMethodId: z.string().min(1),
    captchaToken: z.string(),
    accountType: z.enum(['individual', 'business', 'likely_individual', 'likely_business']).optional(),
  })
  .strict();

export type FlightCheckoutPayload = z.infer<typeof flightCheckoutPayloadSchema>;
export type FlightCheckoutLine = z.infer<typeof flightCheckoutLineSchema>;
export type FlightCheckoutWithRegistrationPayload = z.infer<typeof flightCheckoutWithRegistrationPayloadSchema>;

export type FlightCheckoutWithRegistrationResponse = {
  user: UserType;
  authentication: { idToken: string; expiresIn: number; accessToken: string; refreshToken?: string } | null;
  payment: { status: string; orderId?: number; clientSecret?: string; message?: string };
};
